import React from 'react';
import { motion } from 'framer-motion';
import { Droplet, HeartCrack, XCircle } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function PainSection() {
  return <section className="py-14 px-4 bg-navy-50" data-id="element-200">
      <div className="max-w-3xl mx-auto space-y-14" data-id="element-201">
        {/* Agitación del Dolor */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="space-y-6" data-id="element-202">
          <div className="flex items-center gap-3 text-red-700 mb-8" data-id="element-203"></div>

          <h3 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 leading-tight" data-id="element-204">
            ¿Cuántas veces te has prometido que este año sería diferente?
          </h3>

          <div className="prose prose-lg prose-navy text-navy-700 prose-p:my-2" data-id="element-205">
            <p data-id="element-206">
              Que esta vez sí ibas a ordenar tus finanzas. Que ibas a ser más
              disciplinado. Que ibas a confiar más en Dios y preocuparte menos.
            </p>
            <p className="font-medium text-navy-900" data-id="element-207">
              Y aun así... aquí estás.
            </p>
            <p data-id="element-208">
              No porque seas débil. No porque no tengas fe. No porque no te
              esfuerces.
            </p>
            <p data-id="element-209">Sino porque hay algo que nadie te enseñó a ver:</p>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy-100 my-8 italic text-xl text-navy-800 font-serif border-l-4 border-l-gold-500" data-id="element-210">
              Detrás de cada problema con el dinero, hay una creencia herida.
              Detrás de cada lucha espiritual, hay una identidad rota. Detrás de
              cada fracaso de disciplina, hay una herida de la que nunca
              hablaste.
            </div>
            <p data-id="element-211">
              Y esas tres heridas se alimentan entre sí. Una destruye a la otra.
              Y el ciclo no para... hasta que alguien te ayuda a verlas.
            </p>
          </div>
        </motion.div>

        {/* Amplificación Emocional */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="space-y-6" data-id="element-212">
          <div className="flex items-center gap-3 text-navy-600 mb-8" data-id="element-213"></div>

          <h3 className="text-3xl font-serif font-bold text-navy-900" data-id="element-214">
            Imagina esto:
          </h3>

          <div className="space-y-6 mt-8" data-id="element-215">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-navy-100 flex gap-4" data-id="element-216">
              <div className="w-2 h-full bg-gold-400 rounded-full shrink-0" data-id="element-217"></div>
              <p className="text-navy-700" data-id="element-218">
                Un hombre que ama a Dios con todo su corazón, pero en el fondo
                siente que no merece sus bendiciones. Entonces, cada vez que el
                dinero llega, lo gasta sin control — como si inconscientemente
                lo rechazara.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-navy-100 flex gap-4" data-id="element-219">
              <div className="w-2 h-full bg-gold-400 rounded-full shrink-0" data-id="element-220"></div>
              <p className="text-navy-700" data-id="element-221">
                Una mujer con disciplina de acero para los demás, pero incapaz
                de mantener un hábito para ella misma. Porque en algún lugar
                aprendió que cuidarse era egoísmo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-navy-100 flex gap-4" data-id="element-222">
              <div className="w-2 h-full bg-gold-400 rounded-full shrink-0" data-id="element-223"></div>
              <p className="text-navy-700" data-id="element-224">
                Una persona que reza todos los días pero siente a Dios como un
                juez distante, no como un Padre cercano. Y esa distancia la
                paraliza espiritualmente.
              </p>
            </div>
          </div>

          <div className="mt-8 text-xl text-navy-900 font-medium text-center" data-id="element-225">
            <p data-id="element-226">¿Te suena familiar alguno de estos?</p>
            <p className="mt-2 text-navy-600 font-normal" data-id="element-227">
              Porque no son historias de fracaso. Son historias de heridas sin
              sanar.
            </p>
          </div>
        </motion.div>

        {/* Por qué nada ha funcionado */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="space-y-6 bg-navy-900 text-white p-8 md:p-12 rounded-3xl shadow-xl" data-id="element-228">
          <div className="flex items-center gap-3 text-gold-400 mb-6" data-id="element-229">
            <XCircle className="w-6 h-6" data-id="element-230" />
            <h2 className="text-sm font-bold tracking-widest uppercase" data-id="element-231">
              Por qué nada de lo que has intentado ha funcionado
            </h2>
          </div>

          <p className="text-xl text-navy-100 mb-8" data-id="element-232">
            Probablemente ya intentaste algo.
          </p>

          <ul className="space-y-4 text-navy-200 mb-8" data-id="element-233">
            <li className="flex items-start gap-3" data-id="element-234">
              <span className="text-gold-400 mt-1" data-id="element-235">✕</span>
              <span data-id="element-236">
                Un libro de finanzas que te dio una fórmula pero no entendió tu
                historia.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-237">
              <span className="text-gold-400 mt-1" data-id="element-238">✕</span>
              <span data-id="element-239">
                Un predicador poderoso que te llenó de fuego... que duró tres
                días.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-240">
              <span className="text-gold-400 mt-1" data-id="element-241">✕</span>
              <span data-id="element-242">
                Un coach de hábitos que te dio un sistema que abandonaste en la
                segunda semana.
              </span>
            </li>
          </ul>

          <div className="space-y-2 text-lg" data-id="element-243">
            <p className="font-semibold text-white" data-id="element-244">
              No fallaste esas herramientas.
            </p>
            <p className="text-navy-200" data-id="element-245">
              Esas herramientas fallaron porque atacaron los síntomas, no la
              raíz.
            </p>
            <p className="text-navy-200" data-id="element-246">
              Porque tu problema con el dinero no empezó cuando perdiste ese
              trabajo. Empezó mucho antes — en lo que viste, lo que viviste, lo
              que te dijeron que merecías.
            </p>
            <p className="text-gold-300 font-medium italic mt-4" data-id="element-247">
              Y mientras esa raíz siga intacta, cualquier solución superficial
              durará exactamente lo que dura: poco.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}